import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import RateReviewIcon from "@mui/icons-material/RateReview";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import { LoadingButton } from "@mui/lab";
import { Box, Link, Stack, Typography } from "@mui/material";
import { graphql } from "babel-plugin-relay/macro";
import { useContext } from "react";
import { useMutation } from "react-relay";
import { InternReadyToSubmitCardMutation } from "./__generated__/InternReadyToSubmitCardMutation.graphql";
import SnackbarContext from "../../../contexts/SnackbarContext";
import InternCard from "../components/InternCard";
import { PipelineRow, getPipelineSubmitState } from "./submitState";

const submitMutation = graphql`
  mutation InternReadyToSubmitCardMutation(
    $brandId: ID!
    $pipelineId: ID!
    $platform: String!
  ) {
    submitForReview(
      brandId: $brandId
      pipelineId: $pipelineId
      platform: $platform
    ) {
      success
      message
    }
  }
`;

interface Props {
  brandId: string;
  pipeline: PipelineRow;
  onSubmitted?: () => void;
}

const circleCiUrlFor = (platform: string) =>
  platform === "android"
    ? "https://app.circleci.com/pipelines/github/reactivapp-kin/kinn4a?filter=all"
    : "https://app.circleci.com/pipelines/github/reactivapp-kin/kinn-ios?filter=all";

const CARD_COPY = {
  ios: {
    ready: {
      title: "Ready to submit",
      body: "Uploaded to App Store Connect. Submit when metadata looks right.",
      cta: "Submit for Review",
    },
    retry: {
      title: "Submit failed — retry",
      body: "Uploaded to App Store Connect. Retrying re-syncs metadata before submitting.",
      cta: "Retry Submit",
    },
    stuck: {
      title: "Post-build sync didn't finish",
      body: "The binary is on App Store Connect but automatic processing stalled. Submitting syncs metadata first.",
      cta: "Sync & Submit",
    },
  },
  android: {
    ready: {
      title: "Ready to submit",
      body: "Uploaded to Google Play as a draft. Submit when metadata looks right.",
      cta: "Submit for Review",
    },
    retry: {
      title: "Submit failed — retry",
      body: "Draft is on Google Play. Retrying re-syncs listing metadata before completing the release.",
      cta: "Retry Submit",
    },
    stuck: {
      title: "Post-build sync didn't finish",
      body: "The AAB is on Google Play but automatic processing stalled. Submitting syncs metadata first.",
      cta: "Sync & Submit",
    },
  },
} as const;

const InternReadyToSubmitCard = ({ brandId, pipeline, onSubmitted }: Props) => {
  const snackbarContext = useContext(SnackbarContext);
  const [submitForReview, isSubmitting] =
    useMutation<InternReadyToSubmitCardMutation>(submitMutation);

  const submitState = getPipelineSubmitState(pipeline) ?? "ready";
  const platformKey = pipeline.platform === "android" ? "android" : "ios";
  const copy = CARD_COPY[platformKey][submitState];
  const isFailed = submitState === "retry";
  const version = pipeline.appVersion || "—";
  const build = pipeline.appBuildNumber;
  const storeLabel =
    platformKey === "android" ? "Google Play" : "App Store Review";

  const handleSubmit = () => {
    submitForReview({
      variables: {
        brandId,
        pipelineId: pipeline.id,
        platform: platformKey,
      },
      onCompleted: (data) => {
        if (!data.submitForReview?.success) {
          snackbarContext?.openSnackbar(
            data.submitForReview?.message || "Submit failed",
            "error"
          );
        } else {
          snackbarContext?.openSnackbar(
            data.submitForReview.message || "Submitted for review",
            "success"
          );
          onSubmitted?.();
        }
      },
      onError: () => {
        snackbarContext?.openSnackbar("Submit failed", "error");
      },
    });
  };

  return (
    <InternCard variant="compact">
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ xs: "stretch", sm: "center" }}
        justifyContent="space-between"
      >
        <Stack
          direction="row"
          spacing={1.75}
          alignItems="flex-start"
          sx={{ minWidth: 0 }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 40,
              height: 40,
              borderRadius: 1.25,
              flexShrink: 0,
              bgcolor: isFailed
                ? "rgba(239, 68, 68, 0.1)"
                : "rgba(245, 158, 11, 0.12)",
            }}
          >
            <RateReviewIcon
              sx={{
                fontSize: 20,
                color: isFailed ? "#dc2626" : "#b45309",
              }}
            />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="caption"
              sx={{
                display: "block",
                color: "var(--intern-text-tertiary)",
                fontSize: "0.6875rem",
                fontWeight: 600,
                letterSpacing: "0.04em",
                textTransform: "uppercase",
                mb: 0.25,
              }}
            >
              {storeLabel}
            </Typography>
            <Typography
              variant="subtitle1"
              sx={{
                color: "var(--intern-text-primary)",
                fontWeight: 700,
                fontSize: "0.9375rem",
                lineHeight: 1.25,
                mb: 0.35,
              }}
            >
              {copy.title}
            </Typography>
            {isFailed && pipeline.submitOutcomeDetail ? (
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mb: 0.75,
                  color: "#dc2626",
                  fontSize: "0.6875rem",
                  lineHeight: 1.35,
                }}
              >
                {pipeline.submitOutcomeDetail}
              </Typography>
            ) : null}
            <Typography
              sx={{
                color: "var(--intern-text-primary)",
                fontWeight: 700,
                fontSize: "1.25rem",
                fontVariantNumeric: "tabular-nums",
                lineHeight: 1.2,
                mb: 0.5,
              }}
            >
              v{version}
              {build ? (
                <Box
                  component="span"
                  sx={{
                    color: "var(--intern-text-tertiary)",
                    fontWeight: 500,
                    fontSize: "0.9375rem",
                    ml: 0.75,
                  }}
                >
                  ({build})
                </Box>
              ) : null}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "var(--intern-text-secondary)",
                fontSize: "0.75rem",
                lineHeight: 1.4,
              }}
            >
              {copy.body}
            </Typography>
          </Box>
        </Stack>

        <Stack
          direction={{ xs: "row", sm: "column" }}
          spacing={1}
          alignItems={{ xs: "center", sm: "stretch" }}
          sx={{ flexShrink: 0, minWidth: { sm: 180 } }}
        >
          <LoadingButton
            variant="contained"
            loading={isSubmitting}
            startIcon={<RocketLaunchIcon />}
            onClick={handleSubmit}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              fontSize: "0.8125rem",
              px: 2,
              py: 1,
              boxShadow: "none",
              bgcolor: "var(--intern-accent)",
              "&:hover": {
                bgcolor: "var(--intern-accent)",
                filter: "brightness(1.08)",
                boxShadow: "none",
              },
            }}
          >
            {copy.cta}
          </LoadingButton>
          <Link
            href={circleCiUrlFor(platformKey)}
            target="_blank"
            rel="noopener noreferrer"
            underline="hover"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.5,
              color: "var(--intern-text-tertiary)",
              fontSize: "0.6875rem",
              fontWeight: 600,
            }}
          >
            View in CircleCI
            <OpenInNewIcon sx={{ fontSize: 12 }} />
          </Link>
        </Stack>
      </Stack>
    </InternCard>
  );
};

export default InternReadyToSubmitCard;
